import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  getRetosActivos,
  getRetosPopulares,
  getRetosDestacados,
  getRetosByPeriodo,
  retoPeriodoLabels,
  retoPeriodoIcons,
  retoPeriodoColors,
  recompensaConfig,
  retosDashboard,
  formatRetoCount,
  formatTiempoRestante,
  type RetoClub,
  type RetoPeriodo,
  type TipoRecompensa,
} from '@/mocks/rfwClubChallenges';

/* ─── Reto Card Compact ─── */
function RetoCompactCard({ reto, pos }: { reto: RetoClub; pos: number }) {
  const periodoConfig = retoPeriodoColors[reto.periodo];
  return (
    <Link
      to="/retos-clubes"
      className="flex items-center gap-3 p-3 rounded-xl bg-background-50 hover:bg-background-100/70 transition-colors group cursor-pointer border border-background-100"
    >
      <div className="shrink-0">
        {pos === 1 ? <span className="text-base">🔥</span>
         : pos === 2 ? <span className="text-base">⚡</span>
         : pos === 3 ? <span className="text-base">⭐</span>
         : (
            <span className="w-6 h-6 rounded-full bg-background-100 flex items-center justify-center text-[11px] font-extrabold text-foreground-400">
              {pos}
            </span>
         )}
      </div>
      <div className={`w-9 h-9 rounded-lg ${periodoConfig.badge.split(' ')[0]} flex items-center justify-center shrink-0`}>
        <i className={`${retoPeriodoIcons[reto.periodo]} ${periodoConfig.text} text-sm`}></i>
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold text-foreground-950 truncate group-hover:text-accent-600 transition-colors">{reto.titulo}</p>
        <div className="flex items-center gap-1.5">
          <span className={`text-[9px] font-medium ${periodoConfig.text}`}>{retoPeriodoLabels[reto.periodo]}</span>
          <span className="text-[10px] text-foreground-400 truncate">· {reto.clubNombre}</span>
        </div>
      </div>
      <div className="shrink-0 flex flex-col items-end gap-0.5">
        <span className="text-sm font-extrabold text-foreground-950">{formatRetoCount(reto.participantes)}</span>
        <span className="text-[10px] text-foreground-400">participantes</span>
      </div>
    </Link>
  );
}

/* ─── Reto Card Grande (featured) ─── */
function RetoFeaturedCard({ reto }: { reto: RetoClub }) {
  const periodoConfig = retoPeriodoColors[reto.periodo];
  const tiempoRestante = formatTiempoRestante(reto.fechaFin);
  const porcentaje = reto.objetivo > 0 ? Math.round((reto.progresoGlobal / (reto.objetivo * reto.participantes * 0.6)) * 100) : 0;
  const progressPercent = Math.min(porcentaje, 100);

  return (
    <div className="bg-background-50 rounded-2xl overflow-hidden border border-background-200/70 hover:border-background-300/60 transition-all duration-200 group">
      <div className="p-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold border ${periodoConfig.badge} ${periodoConfig.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${periodoConfig.dot}`}></span>
            {retoPeriodoLabels[reto.periodo]}
          </span>
          <span className="text-[10px] font-semibold text-foreground-500 flex items-center gap-1">
            <i className="ri-time-line text-[10px]"></i>
            {tiempoRestante}
          </span>
        </div>

        {/* Title & Description */}
        <h4 className="text-sm font-extrabold text-foreground-950 mb-1 group-hover:text-accent-600 transition-colors">{reto.titulo}</h4>
        <p className="text-[11px] text-foreground-500 leading-relaxed mb-3">{reto.descripcion}</p>

        {/* Club info */}
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 rounded-md bg-background-100 flex items-center justify-center">
            <i className="ri-group-line text-[10px] text-foreground-400"></i>
          </div>
          <span className="text-[10px] text-foreground-500">{reto.clubNombre}</span>
          <span className="text-[10px] text-foreground-300">·</span>
          <span className="text-[10px] font-semibold text-foreground-700">{reto.categoria}</span>
        </div>

        {/* Progress bar */}
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] text-foreground-500">Progreso global</span>
            <span className="text-[10px] font-bold text-foreground-700">{progressPercent}%</span>
          </div>
          <div className="w-full h-1.5 bg-background-200 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-700 ${periodoConfig.bg}`}
              style={{ width: `${progressPercent}%` }}
            ></div>
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="bg-background-100 rounded-lg p-2 text-center">
            <span className="block text-sm font-extrabold text-foreground-950">{formatRetoCount(reto.participantes)}</span>
            <span className="text-[9px] text-foreground-400">Participantes</span>
          </div>
          <div className="bg-background-100 rounded-lg p-2 text-center">
            <span className="block text-sm font-extrabold text-foreground-950">{formatRetoCount(reto.completados)}</span>
            <span className="text-[9px] text-foreground-400">Completaron</span>
          </div>
          <div className="bg-background-100 rounded-lg p-2 text-center">
            <span className="block text-sm font-extrabold text-accent-600">+{reto.scoreBase}</span>
            <span className="text-[9px] text-foreground-400">Pts base</span>
          </div>
        </div>

        {/* Rewards pills */}
        <div className="flex flex-wrap gap-1 mb-3">
          {reto.recompensas.map((rec, i) => {
            const cfg = recompensaConfig[rec.tipo];
            return (
              <span key={i} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold ${cfg.bg} ${cfg.color}`}>
                <i className={`${cfg.icon} text-[8px]`}></i>
                {cfg.label}
              </span>
            );
          })}
        </div>

        {/* Patrocinador badge */}
        {reto.patrocinador && (
          <div className="flex items-center gap-2 p-2 rounded-lg bg-violet-50 border border-violet-100">
            {reto.patrocinadorLogo && (
              <img src={reto.patrocinadorLogo} alt={reto.patrocinador} className="w-6 h-6 rounded-md object-cover" loading="lazy" />
            )}
            <div>
              <span className="text-[9px] text-violet-500">Patrocinado por</span>
              <p className="text-[10px] font-bold text-violet-700">{reto.patrocinador}</p>
            </div>
          </div>
        )}

        {/* CTA */}
        <Link
          to="/retos-clubes"
          className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap"
        >
          <i className="ri-sword-line"></i>
          Ver Reto
        </Link>
      </div>
    </div>
  );
}

type LandingTab = 'activos' | 'populares';

export default function RetosClubesSection() {
  const [activeTab, setActiveTab] = useState<LandingTab>('activos');

  const activos = useMemo(() => getRetosActivos().filter(r => r.destacado).slice(0, 4), []);
  const populares = useMemo(() => getRetosPopulares().slice(0, 4), []);
  const allActivos = useMemo(() => getRetosActivos().slice(0, 6), []);
  const allPopulares = useMemo(() => getRetosPopulares().slice(0, 6), []);

  const tabs: { id: LandingTab; label: string; icon: string }[] = [
    { id: 'activos', label: 'Retos Activos', icon: 'ri-sword-line' },
    { id: 'populares', label: 'Retos Populares', icon: 'ri-fire-line' },
  ];

  const featuredData = activeTab === 'activos' ? activos : populares;
  const listData = activeTab === 'activos' ? allActivos : allPopulares;

  return (
    <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-orange-100 text-orange-700 text-xs font-semibold mb-4">
            <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse"></span>
            RETOS DE CLUBES
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            Mantené la <span className="text-accent-600">Actividad Constante</span> en tu Club
          </h2>
          <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
            Retos diarios, semanales, mensuales, especiales y patrocinados. Completalos, sumá puntos, ganá insignias, premios y entradas. Simple y divertido.
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10">
          {[
            { label: 'Retos Activos', value: retosDashboard.activosAhora.toString(), icon: 'ri-sword-line', color: 'text-orange-600 bg-orange-50' },
            { label: 'Participantes', value: formatRetoCount(retosDashboard.participantesTotales), icon: 'ri-user-line', color: 'text-emerald-600 bg-emerald-50' },
            { label: 'Retos Completados', value: formatRetoCount(retosDashboard.retosCompletados), icon: 'ri-check-double-line', color: 'text-sky-600 bg-sky-50' },
            { label: 'Score Generado', value: formatRetoCount(retosDashboard.scoreGenerado), icon: 'ri-flashlight-line', color: 'text-accent-600 bg-accent-50' },
            { label: 'Clubes Participando', value: retosDashboard.clubesParticipando.toString(), icon: 'ri-group-line', color: 'text-violet-600 bg-violet-50' },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-background-200/70">
              <div className={`w-9 h-9 rounded-full ${stat.color} flex items-center justify-center shrink-0`}>
                <i className={`${stat.icon} text-sm`}></i>
              </div>
              <div>
                <p className="text-lg font-bold text-foreground-950">{stat.value}</p>
                <p className="text-[10px] text-foreground-500">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-center mb-8">
          <div className="inline-flex bg-background-100 rounded-full p-1 border border-background-200/70">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'bg-accent-500 text-background-50 shadow-sm'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                <i className={`${tab.icon} text-[10px]`}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Featured Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {featuredData.map((reto) => (
            <RetoFeaturedCard key={reto.id} reto={reto} />
          ))}
        </div>

        {/* List */}
        <div className="space-y-2 mb-8">
          {listData.map((reto, i) => (
            <RetoCompactCard key={reto.id} reto={reto} pos={i + 1} />
          ))}
        </div>

        {/* Periodo types */}
        <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          {([
            { periodo: 'diario' as RetoPeriodo, label: 'Diarios', icon: 'ri-sun-line', color: 'bg-sky-100 text-sky-600', count: retosDashboard.diarios },
            { periodo: 'semanal' as RetoPeriodo, label: 'Semanales', icon: 'ri-calendar-check-line', color: 'bg-emerald-100 text-emerald-600', count: retosDashboard.semanales },
            { periodo: 'mensual' as RetoPeriodo, label: 'Mensuales', icon: 'ri-calendar-2-line', color: 'bg-amber-100 text-amber-600', count: retosDashboard.mensuales },
            { periodo: 'especial' as RetoPeriodo, label: 'Especiales', icon: 'ri-star-line', color: 'bg-rose-100 text-rose-600', count: retosDashboard.especiales },
            { periodo: 'patrocinado' as RetoPeriodo, label: 'Patrocinados', icon: 'ri-hand-coin-line', color: 'bg-violet-100 text-violet-600', count: retosDashboard.patrocinados },
          ]).map((t) => (
            <div key={t.periodo} className="text-center p-3 rounded-xl bg-background-50 border border-background-200/70 hover:border-accent-200/60 transition-all duration-200 cursor-pointer">
              <div className={`w-9 h-9 rounded-full ${t.color} flex items-center justify-center mx-auto mb-1.5`}>
                <i className={`${t.icon} text-sm`}></i>
              </div>
              <span className="text-[10px] font-semibold text-foreground-700">{t.label}</span>
              <span className="block text-lg font-extrabold text-foreground-950 mt-0.5">{t.count}</span>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-10">
          <Link
            to="/retos-clubes"
            className="inline-flex items-center gap-2 px-6 py-3 bg-accent-500 hover:bg-accent-600 text-background-50 font-semibold rounded-full transition-all duration-200 text-sm whitespace-nowrap"
          >
            <i className="ri-sword-line"></i>
            Ver Todos los Retos
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        {/* Regla Final */}
        <div className="mt-10 p-6 rounded-2xl bg-orange-50 border border-orange-100 text-center">
          <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-sword-line text-lg text-background-50"></i>
          </div>
          <p className="text-sm font-bold text-foreground-950 mb-1">
            Los retos deben ser simples y divertidos.
          </p>
          <p className="text-xs text-foreground-600 max-w-lg mx-auto">
            Comentar 5 publicaciones, compartir 3 posts, invitar 2 amigos, participar en cabina, mantener la racha. Nada complicado — acciones que cualquier miembro puede hacer y que mantienen viva la comunidad del club todos los días.
          </p>
        </div>
      </div>
    </section>
  );
}